import type { LaneStretch } from '../../hazards/hazardRules';
import { stretchesOverlap } from '../terrain/roofRunUp';

/**
 * The lanes along the generated track (run distance), as obstacles with a walkable top need them
 * (level/terrain/roofRunUp.ts) – also across segment boundaries:
 *   busy      placed obstacles until a runner who got past them is back on the road, terrain that is no
 *             plain road, the ground moving hazards need – a container's run-up must stay clear of them;
 *   reserved  the drop zones behind placed containers – nothing placed later may stand in them.
 * Immutable: the TrackManager keeps one per branch of an intersection, together with the SequenceValidator's
 * row history.
 */
export class LaneRunUps {
  static readonly EMPTY = new LaneRunUps([], []);

  private constructor(
    private readonly busy: readonly LaneStretch[],
    private readonly reserved: readonly LaneStretch[],
  ) {}

  /** With these busy stretches (run distance) added. */
  with(added: readonly LaneStretch[]): LaneRunUps {
    return added.length === 0 ? this : new LaneRunUps([...this.busy, ...added], this.reserved);
  }

  /** With these stretches (run distance) kept free for what comes later. */
  reserving(zones: readonly LaneStretch[]): LaneRunUps {
    return zones.length === 0 ? this : new LaneRunUps(this.busy, [...this.reserved, ...zones]);
  }

  /** Without the stretches that end before `distance` – nothing placed from there on looks back that far. */
  forgetBefore(distance: number): LaneRunUps {
    const busy = this.busy.filter((s) => s.zTo >= distance);
    const reserved = this.reserved.filter((s) => s.zTo >= distance);
    return busy.length === this.busy.length && reserved.length === this.reserved.length ? this : new LaneRunUps(busy, reserved);
  }

  /** Nothing busy in `stretch` (run distance). */
  isFree(stretch: LaneStretch): boolean {
    return !this.busy.some((s) => stretchesOverlap(s, stretch));
  }

  /** `stretch` reaches into a zone kept free (run distance). */
  isReserved(stretch: LaneStretch): boolean {
    return this.reserved.some((s) => stretchesOverlap(s, stretch));
  }
  /** Number of remembered stretches (bounded by forgetBefore). */
  get size(): number {
    return this.busy.length + this.reserved.length;
  }
}
